import { ref, computed, getCurrentInstance, ComponentInternalInstance } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入 compositions
import { UploadFile } from '@/renderer/components/SpacePage/compositions/uploadHandler';
import createHandler from '@/renderer/components/SpacePage/compositions/createHandler';

// 引入工具类
import { openTeamPayDialog, PAY_SOURCE } from '@/renderer/scripts/composition/dialogHandler';
import tips from '@/renderer/scripts/utils/tips';
import { getSpeedAndUnit } from '@/renderer/scripts/utils/tools/packages/unit';
import ipc, { IpcEvents } from '@/renderer/scripts/utils/ipc';
import smh, { SMH } from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import { IS_BROWSER, IS_DEV } from '@/common/constant/platform';

// 单次提交的总量数据
let uploadFormData = new FormData();
export default function collectFileHandler(collectTask: SMH.CollectTaskData) {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;

  // 限制添加5G文件
  const size5G = 5 * 1024 * 1024 * 1024;
  const spaceId = computed(() => collectTask.spaceId);

  // 可添加的文件类型
  const acceptMediaTypes = computed<string>(() => {
    let result = '';
    const union = ',';
    const acceptMap: {
      [key: string]: any;
    } = {
      ppt: [
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ],
      excel: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      word: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      pdf: ['application/pdf'],
    };
    const { fileType } = collectTask;
    if (fileType && fileType.length > 0 && fileType[0] !== '') {
      fileType.forEach(key => {
        result += acceptMap[key].join(union) + union;
      });
    } else {
      return result;
    }
    return result.substring(0, result.length - 1);
  });

  // 支持的文件后缀
  const supportFileType = computed<Array<string>>(() => {
    const fileTypeMap: {
      [key: string]: Array<string>;
    } = {
      word: ['doc', 'docx'],
      ppt: ['ppt', 'pptx'],
      excel: ['xls', 'xlsx'],
      pdf: ['pdf'],
    };
    let result: string[] = [];
    collectTask.fileType.forEach(key => {
      result = [...result, ...fileTypeMap[key]];
    });
    return result;
  });

  const collectingFileList = ref<any>([]);

  const { methods: createMethods } = createHandler(spaceId);
  const methods = {
    // 点击（添加）按钮进行选择操作
    // @next addFiles
    async chooseFile() {
      if (IS_BROWSER) {
        const { fileInput } = currComponent.refs;
        (fileInput as HTMLElement).click();
      } else {
        const { files } = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
          event: 'selectFiles',
        });
      }
    },
    // 判断是否支持的文件类型
    isSupportType(suffix: string) {
      let result = false;
      const isSupport = supportFileType.value.length ? supportFileType.value.includes(suffix) : true;
      if (!isSupport) {
        tips.error(
          i18n_t('当前任务仅支持{slot1}类型的文件', {
            slot1: supportFileType.value.join(','),
          }),
        );
        result = false;
      } else {
        result = true;
      }
      return result;
    },
    // 添加多个文件
    async addFiles(files: FileList | null) {
      await methods.dropFiles(files);
    },
    // 拖拽添加多个文件
    async dropFiles(files: FileList | null) {
      // 处理添加文件
      if (files) {
        // 计算待添加列表的大小（新添加的文件 + 已添加的)
        let totalSize = 0;
        collectingFileList.value.forEach((item: { size: number }) => {
          totalSize += item.size;
        });
        for (const file of files) {
          totalSize += Number(file.size);
        }
        const needUpgrade = await methods.checkIfUpgrade(totalSize, spaceId.value);
        if (needUpgrade) return;
        // eslint-disable-next-line no-restricted-syntax
        a: for (const file of files) {
          const { name, size, type } = file;
          // 文件大小校验
          if (size > size5G) {
            tips.error(i18n_t('单文件仅支持5G以内大小'));
            continue a;
          }
          // 同名文件处理
          for (let i = 0; i < collectingFileList.value.length; i++) {
            const { name: existName } = collectingFileList.value[i];
            if (name === existName) {
              tips.error(i18n_t('已有同名文件,删除后再重新添加'));
              continue a;
            }
          }
          uploadFormData.append('file[]', file);
          const hasSuffix = name.indexOf('.') !== -1;
          const suffix = hasSuffix ? (name.substr(name.lastIndexOf('.') + 1) as string) : '';
          if (!methods.isSupportType(suffix)) {
            return;
          }
          const { speed, unit } = getSpeedAndUnit(size);
          collectingFileList.value.push({
            name,
            formatSize: `${speed}${unit}`,
            type,
            size,
            creationTime: new Date().getTime(),
          });
        }
      }
    },
    //  移除文件
    deleteFile(index: number) {
      collectingFileList.value.splice(index, 1);
    },
    //  提交action
    async prepareFileForm(ruleData: { [key: string]: string }): Promise<{
      filesParam: UploadFile[];
      uploadPath: string;
    }> {
      const files = uploadFormData.getAll('file[]') as unknown as FileList;
      // 重命名文件
      let prefix = '';
      // 自动生成的用户文件夹
      // eslint-disable-next-line no-restricted-syntax
      for (const key in ruleData) {
        prefix += `${ruleData[key].trim()}-`;
      }
      const inform = new FormData();
      for (const file of files) {
        const uploadFiles = inform.getAll('file[]') as UploadFile[];
        const fileNameArr = uploadFiles.map(file => file.name);
        for (const collectingFile of collectingFileList.value) {
          const fileName = `${prefix}${file.name}`;
          if (file.name === collectingFile.name && !fileNameArr.includes(fileName)) {
            inform.append('file[]', file, fileName);
          }
        }
      }
      // 添加文件
      const currPath = [collectTask.path];
      const subDirPath = prefix.substring(0, prefix.length - 1);
      const uploadPath = [collectTask.path, subDirPath].join('/');
      // 创建文件夹去掉后缀
      await createMethods.onCreateFolder(subDirPath, currPath, true, 'ask');
      const filesParam = inform.getAll('file[]') as UploadFile[];
      return {
        filesParam,
        uploadPath,
      };
    },
    resetFormData() {
      uploadFormData = new FormData();
    },
    async checkIfUpgrade(size: number, spaceId: string) {
      try {
        const res = await smh.commonAPI.checkFileStorage({
          size,
          spaceId,
          collectToken: collectTask.code,
        });
        if (res.status === 204) return false;
      } catch (err: any) {
        if (err?.code === 'QuotaLimitReached' || err?.code === 'LibraryQuotaLimitReached') {
          reporter.report('value_added', {
            space_show: 'groups_upload_copy_fixed_popup',
          });
          openTeamPayDialog(PAY_SOURCE.UPLOAD_FILES, {
            isCollectFile: true,
            collectFileRole: collectTask.userId === store.getters.userId ? 'isOwner' : '',
          });
        } else {
          tips.error(err as MsgError);
        }
        return true;
      }
      return false;
    },
  };
  return {
    collectingFileList,
    acceptMediaTypes,
    methods,
  };
}
